import React, { Component, Fragment } from "react";
import s from "./styles.scss";
import { Table } from "reactstrap";
import { faEdit, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import MembresiaPlantilla, {
  GerenteRegional,
  LiderComercial,
  AsesorClub
} from "../../../../types/MembresiaPlantilla";
import { Styled } from "../../../../toolkit/decorators/Artifact";
import BaseButtonWithIconAndTooltip from "../../../../toolkit/baseButtonWithIconAndTooltip";
import { formatIsoDate } from "../../../helpers/formatters";

interface Props {
  plantilla: MembresiaPlantilla;
  filtros: {
    tipoBusqueda: string;
    region: number;
    club: number;
  };
  onEditGerente: (gerente: GerenteRegional) => void;
  onDeleteGerente: (gerente: GerenteRegional) => void;
  onEditLider: (lider: LiderComercial) => void;
  onDeleteLider: (lider: LiderComercial) => void;
  onEditAsesor: (asesor: AsesorClub, lider: LiderComercial) => void;
  onDeleteAsesor: (asesor: AsesorClub, lider: LiderComercial) => void;
  onAddAsesor: (lider: LiderComercial) => void;
  onCambiarMetas: (gerente: GerenteRegional) => void;
  onCambiarMetasClub: (lider: LiderComercial) => void;
  onCambiarMetasAsesores: (lider: LiderComercial) => void;
}

@Styled(s)
export default class ConfiguracionPlantillaTable extends Component<Props> {
  filtrarGerentes = () => {
    const { plantilla, filtros } = this.props;
    if (!plantilla || !plantilla.gerentes) return [];

    // Filtrar por región si está seleccionada
    if (!filtros.tipoBusqueda && filtros.region) {
      return plantilla.gerentes.filter(
        gerente => gerente.numeroRegion == filtros.region
      );
    }

    // Filtrar por club si está seleccionado
    if (filtros.tipoBusqueda === "CLUBES" && filtros.club) {
      return plantilla.gerentes
        .map(gerente => ({
          ...gerente,
          lideres:
            gerente.lideres.filter(lider => lider.idClub == filtros.club) || []
        }))
        .filter(gerente => gerente.lideres.length > 0);
    }

    // Sin filtros, mostrar todo
    return plantilla.gerentes;
  };

  formatNombre = (usuario: LiderComercial | AsesorClub) => {
    const fechaBaja = usuario.fechaBaja
      ? ` - BAJA: ${formatIsoDate(usuario.fechaBaja)}`
      : "";

    return `(${usuario.numeroEmpleado}) ${usuario.nombreEmpleado} ${fechaBaja}`;
  };

  render() {
    const {
      plantilla,
      onEditGerente,
      onDeleteGerente,
      onEditLider,
      onDeleteLider,
      onEditAsesor,
      onDeleteAsesor,
      onAddAsesor,
      onCambiarMetas,
      onCambiarMetasClub,
      onCambiarMetasAsesores
    } = this.props;

    if (!plantilla || !plantilla.gerentes) {
      return null;
    }

    const gerentesFiltrados = this.filtrarGerentes();

    if (gerentesFiltrados.length === 0) {
      return (
        <div className="alert alert-info">
          No se encontraron resultados para los filtros seleccionados.
        </div>
      );
    }

    return (
      <Table bordered responsive size="sm">
        <tbody>
          {/* Gerentes Regionales */}
          {gerentesFiltrados.map((gerente, gerenteIdx) => (
            <Fragment key={`gerente-${gerenteIdx}`}>
              <tr className={s.regionRow}>
                <td>
                  <div className={s.regionTitle}>
                    Región {gerente.numeroRegion}
                  </div>
                </td>
                <td className={s.metaTitlesCell}>
                  <span className={s.metaTitle}>M. Vnt.</span>
                  <span className={s.metaTitle}>M. Rctv.</span>
                  <span className={s.metaTitle}>M. Cons.</span>
                </td>
                <td></td>
              </tr>
              <tr className={s.gerenteRow}>
                <td className={s.gerenteName}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    <div style={{ width: "calc(100% - 176px)" }}>
                      <strong>Gerente regional:</strong>{" "}
                      {gerente.nombreEmpleado || (
                        <em className={s.emptyGerente}>Sin gerente regional</em>
                      )}
                    </div>
                    <div style={{ width: "160px", textAlign: "right" }}>
                      {plantilla.id && (
                        <span
                          className={s.cambiarMeta}
                          onClick={() => onCambiarMetas(gerente)}
                        >
                          Metas Presupuestadas
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className={s.metasCell}>
                  <span className={s.metaValue}>{gerente.metaVentas || 0}</span>
                  <span className={s.metaValue}>
                    {gerente.metaReactivaciones || 0}
                  </span>
                  <span className={s.metaValue}>
                    {gerente.metaConsolidada || 0}
                  </span>
                </td>
                <td className={s.actionsCell}>
                  {!plantilla.confirmado && (
                    <BaseButtonWithIconAndTooltip
                      id={`gerente-del-${gerenteIdx}`}
                      icon={faTrash}
                      color="#dc3545"
                      tooltipTitle="Eliminar Gerente"
                      onClick={() => onDeleteGerente(gerente)}
                    />
                  )}
                  <BaseButtonWithIconAndTooltip
                    id={`gerente-edit-${gerenteIdx}`}
                    icon={faEdit}
                    color="#ffc107"
                    tooltipTitle="Editar Gerente"
                    onClick={() => onEditGerente(gerente)}
                  />
                </td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
              </tr>

              {/* Líderes Comerciales */}
              {gerente.lideres &&
                gerente.lideres.map((lider, liderIdx) => (
                  <Fragment key={`lider-${gerenteIdx}-${liderIdx}`}>
                    <tr className={s.clubRow}>
                      <td className={s.clubName} colSpan={3}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <div style={{ width: "calc(100% - 310px)" }}>
                            <strong>Club:</strong> {lider.nombreClub}
                          </div>
                          <div style={{ width: "128px", textAlign: "right" }}>
                            {plantilla.id && plantilla.confirmado && (
                              <span
                                className={s.cambiarMeta}
                                onClick={() => onCambiarMetasClub(lider)}
                              >
                                Metas de Líder
                              </span>
                            )}
                          </div>
                          <div style={{ width: "164px", textAlign: "right" }}>
                            {plantilla.id && plantilla.confirmado && (
                              <span
                                className={s.cambiarMeta}
                                onClick={() => onCambiarMetasAsesores(lider)}
                              >
                                Metas de Asesores
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className={s.liderLabelRow}>
                      <td className={s.liderLabel}>Líder comercial</td>
                      <td className={s.metaTitlesCell}>
                        <span className={s.metaTitle}>M. Vnt.</span>
                        <span className={s.metaTitle}>M. Rctv.</span>
                        <span className={s.metaTitle}>M. Cons.</span>
                      </td>
                      <td></td>
                    </tr>
                    <tr className={s.liderRow}>
                      <td className={s.liderName}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <div
                            className={lider.fechaBaja ? s.baja : ""}
                            style={{ width: "calc(100% - 310px)" }}
                          >
                            {lider.numeroEmpleado ? (
                              <>{this.formatNombre(lider)}</>
                            ) : (
                              <em className={s.emptyLider}>
                                Sin Líder Comercial
                              </em>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={s.metasCell}>
                        <span className={s.metaValue}>
                          {lider.metaVentas || 0}
                        </span>
                        <span className={s.metaValue}>
                          {lider.metaReactivaciones || 0}
                        </span>
                        <span className={s.metaValue}>
                          {lider.metaConsolidada || 0}
                        </span>
                      </td>
                      <td className={s.actionsCell}>
                        <BaseButtonWithIconAndTooltip
                          id={`lider-del-${gerenteIdx}-${liderIdx}`}
                          icon={faTrash}
                          color="#dc3545"
                          tooltipTitle="Eliminar líder"
                          onClick={() => onDeleteLider(lider)}
                        />
                        <BaseButtonWithIconAndTooltip
                          id={`lider-edit-${gerenteIdx}-${liderIdx}`}
                          icon={faEdit}
                          color="#ffc107"
                          tooltipTitle="Editar líder"
                          onClick={() => onEditLider(lider)}
                        />
                      </td>
                    </tr>

                    {/* Asesores de Club */}
                    <tr className={s.asesorLabelRow}>
                      <td className={s.asesorLabel}>Asesores</td>
                      <td className={s.metaTitlesCell}>
                        <span className={s.metaTitle}>M. Vnt.</span>
                        <span className={s.metaTitle}>M. Rctv.</span>
                        <span className={s.metaTitle}>M. Cons.</span>
                      </td>
                      <td className={s.actionsCell}>
                        <BaseButtonWithIconAndTooltip
                          id={`asesor-add-${gerenteIdx}-${liderIdx}`}
                          icon={faPlus}
                          color="#28a745"
                          tooltipTitle="Agregar asesor"
                          onClick={() => onAddAsesor(lider)}
                        />
                      </td>
                    </tr>

                    {lider.asesores &&
                      lider.asesores.map((asesor, idx) => (
                        <tr
                          className={s.asesorRow}
                          key={`asesor-${gerenteIdx}-${liderIdx}-${idx}`}
                        >
                          <td className={s.asesorName}>
                            <span
                              className={
                                asesor.fechaBaja
                                  ? s.baja
                                  : asesor.nuevoIngreso
                                  ? s.nuevoIngreso
                                  : s.asesorNormal
                              }
                            >
                              {this.formatNombre(asesor)}
                            </span>
                          </td>
                          <td className={s.metasCell}>
                            <span className={s.metaValue}>
                              {asesor.metaVentas || 0}
                            </span>
                            <span className={s.metaValue}>
                              {asesor.metaReactivaciones || 0}
                            </span>
                            <span className={s.metaValue}>
                              {asesor.metaConsolidada || 0}
                            </span>
                          </td>
                          <td className={s.actionsCell}>
                            <BaseButtonWithIconAndTooltip
                              id={`asesor-del-${gerenteIdx}-${liderIdx}-${idx}`}
                              icon={faTrash}
                              color="#dc3545"
                              tooltipTitle="Eliminar asesor"
                              onClick={() => onDeleteAsesor(asesor, lider)}
                            />
                            <BaseButtonWithIconAndTooltip
                              id={`asesor-edit-${gerenteIdx}-${liderIdx}-${idx}`}
                              icon={faEdit}
                              color="#ffc107"
                              tooltipTitle="Editar asesor"
                              onClick={() => onEditAsesor(asesor, lider)}
                            />
                          </td>
                        </tr>
                      ))}

                    <tr>
                      <td>&nbsp;</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </Fragment>
                ))}
            </Fragment>
          ))}
        </tbody>
      </Table>
    );
  }
}
